import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import ProductCard from "@/components/ProductCard";
import { Search } from "lucide-react";

export default function Shop() {
  const { categorySlug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (categorySlug) params.category = categorySlug;
    if (search.trim()) params.search = search.trim();
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [categorySlug, search]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 fade-in" data-testid="shop-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="tracking-eyebrow text-[var(--eafg-muted)]">{t("nav.shop")}</p>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-3" data-testid="shop-title">
            {t("shop.title")}
          </h1>
        </div>
        <div className="relative max-w-xs w-full">
          <Search
            size={16}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--eafg-muted)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("shop.searchPlaceholder")}
            className="w-full border-b border-[var(--eafg-line)] bg-transparent pl-9 py-3 focus:outline-none focus:border-[var(--eafg-ink)] tracking-eyebrow text-[0.78rem]"
            data-testid="shop-search-input"
          />
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-3 mb-14" data-testid="shop-filters">
        <button
          onClick={() => navigate("/tienda")}
          className={`tracking-eyebrow px-5 py-2 border transition-colors ${
            !categorySlug
              ? "border-[var(--eafg-ink)] bg-[var(--eafg-ink)] text-white"
              : "border-[var(--eafg-line)] hover:border-[var(--eafg-ink)]"
          }`}
          data-testid="shop-filter-all"
        >
          {t("shop.all")}
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/tienda/${c.slug}`)}
            className={`tracking-eyebrow px-5 py-2 border transition-colors ${
              categorySlug === c.slug
                ? "border-[var(--eafg-ink)] bg-[var(--eafg-ink)] text-white"
                : "border-[var(--eafg-line)] hover:border-[var(--eafg-ink)]"
            }`}
            data-testid={`shop-filter-${c.slug}`}
          >
            {lang === "es" ? c.name_es : c.name_en}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="tracking-eyebrow text-[var(--eafg-muted)]">{t("common.loading")}</p>
      ) : products.length === 0 ? (
        <div className="py-20 text-center">
          <p className="font-serif-display text-3xl">{t("shop.empty")}</p>
          <Link
            to="/tienda"
            className="inline-block mt-6 tracking-eyebrow underline underline-offset-4 hover:text-[var(--eafg-copper)]"
          >
            {t("shop.all")}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-8" data-testid="shop-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
